import React from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  useTheme,
  Typography,
  Box,
} from "@mui/material";
import { Edit, Delete } from "@mui/icons-material";

const DynamicTable = ({ data, columns, onEdit, onDelete }) => {
  const darkMode = JSON.parse(sessionStorage.getItem("darkMode"));

  // Modern dark mode styles
  const tableContainerStyles = {
    border: darkMode 
      ? "1px solid rgba(75, 85, 99, 0.3)" 
      : "1px solid rgba(229, 231, 235, 0.3)",
    mt: 2,
    backgroundColor: darkMode 
      ? "rgba(17, 24, 39, 0.98)" 
      : "rgba(255, 255, 255, 0.98)",
    borderRadius: "12px",
    backdropFilter: "blur(16px)",
    boxShadow: darkMode
      ? "0 25px 50px -12px rgba(0, 0, 0, 0.8), 0 0 0 1px rgba(255, 255, 255, 0.05)"
      : "0 25px 50px -12px rgba(0, 0, 0, 0.25), 0 0 0 1px rgba(0, 0, 0, 0.05)",
    overflow: "hidden",
  };

  const tableHeaderStyles = {
    background: darkMode
      ? "linear-gradient(135deg, rgba(31, 41, 55, 0.95) 0%, rgba(17, 24, 39, 0.95) 50%, rgba(31, 41, 55, 0.95) 100%)"
      : "linear-gradient(135deg, rgba(248, 250, 252, 0.95) 0%, rgba(219, 234, 254, 0.95) 50%, rgba(238, 242, 255, 0.95) 100%)",
    "& .MuiTableCell-root": {
      color: darkMode ? "rgba(243, 244, 246, 1)" : "rgba(17, 24, 39, 1)",
      fontWeight: "600",
      fontSize: "0.875rem",
      borderBottom: darkMode
        ? "1px solid rgba(75, 85, 99, 0.3)"
        : "1px solid rgba(229, 231, 235, 0.3)",
      borderRight: darkMode
        ? "1px solid rgba(75, 85, 99, 0.2)"
        : "1px solid rgba(229, 231, 235, 0.2)",
    },
  };

  const tableBodyStyles = {
    "& .MuiTableCell-root": {
      color: darkMode ? "rgba(243, 244, 246, 1)" : "rgba(17, 24, 39, 1)",
      backgroundColor: darkMode 
        ? "rgba(17, 24, 39, 0.95)" 
        : "rgba(255, 255, 255, 0.95)",
      borderBottom: darkMode
        ? "1px solid rgba(75, 85, 99, 0.2)"
        : "1px solid rgba(229, 231, 235, 0.2)",
      borderRight: darkMode
        ? "1px solid rgba(75, 85, 99, 0.2)"
        : "1px solid rgba(229, 231, 235, 0.2)",
      fontSize: "0.8rem",
    },
    "& .MuiTableRow-root": {
      transition: "all 0.2s ease-in-out",
      "&:hover": {
        backgroundColor: darkMode 
          ? "rgba(31, 41, 55, 0.8)" 
          : "rgba(248, 250, 252, 0.8)",
        transform: "translateY(-1px)",
        boxShadow: darkMode
          ? "0 4px 12px rgba(0, 0, 0, 0.2)"
          : "0 4px 12px rgba(0, 0, 0, 0.1)",
      },
    },
  };

  const iconButtonStyles = {
    color: darkMode ? "rgba(16, 185, 129, 1)" : "rgba(5, 150, 105, 1)",
    backgroundColor: darkMode 
      ? "rgba(31, 41, 55, 0.5)" 
      : "rgba(248, 250, 252, 0.5)",
    borderRadius: "8px",
    margin: "0 2px",
    padding: "6px",
    backdropFilter: "blur(8px)",
    border: darkMode
      ? "1px solid rgba(16, 185, 129, 0.3)"
      : "1px solid rgba(5, 150, 105, 0.3)",
    transition: "all 0.2s ease-in-out",
    "&:hover": {
      backgroundColor: darkMode
        ? "rgba(16, 185, 129, 0.2)"
        : "rgba(5, 150, 105, 0.2)",
      transform: "translateY(-1px) scale(1.05)",
      boxShadow: darkMode
        ? "0 4px 12px rgba(16, 185, 129, 0.3)"
        : "0 4px 12px rgba(5, 150, 105, 0.25)",
    },
  };

  const deleteButtonStyles = {
    color: darkMode ? "rgba(248, 113, 113, 1)" : "rgba(220, 38, 38, 1)",
    backgroundColor: darkMode 
      ? "rgba(31, 41, 55, 0.5)" 
      : "rgba(248, 250, 252, 0.5)",
    borderRadius: "8px",
    margin: "0 2px",
    padding: "6px",
    backdropFilter: "blur(8px)",
    border: darkMode
      ? "1px solid rgba(248, 113, 113, 0.3)"
      : "1px solid rgba(220, 38, 38, 0.3)",
    transition: "all 0.2s ease-in-out",
    "&:hover": {
      backgroundColor: darkMode
        ? "rgba(248, 113, 113, 0.2)"
        : "rgba(220, 38, 38, 0.2)",
      transform: "translateY(-1px) scale(1.05)",
      boxShadow: darkMode
        ? "0 4px 12px rgba(248, 113, 113, 0.3)"
        : "0 4px 12px rgba(220, 38, 38, 0.25)",
    },
  };

  const renderCellContent = (row, column) => {
    const value = row[column.field];

    // Use custom render function if provided
    if (column.render) {
      return column.render(row);
    }

    // Handle File object (like audio or image)
    if (value instanceof File) {
      if (value.type.startsWith("audio/")) {
        return (
          <div style={{ maxWidth: "200px" }}>
            <Typography variant="caption" sx={{
              color: darkMode ? "rgba(156, 163, 175, 1)" : "rgba(107, 114, 128, 1)",
              display: "block",
              marginBottom: "4px",
            }}>
              {value.name}
            </Typography>
            <audio 
              controls 
              src={URL.createObjectURL(value)}
              style={{ width: "100%", height: "30px" }}
            />
          </div>
        );
      } else if (value.type.startsWith("image/")) {
        return (
          <div style={{ textAlign: "center" }}>
            <Typography variant="caption" sx={{
              color: darkMode ? "rgba(156, 163, 175, 1)" : "rgba(107, 114, 128, 1)",
              display: "block",
              marginBottom: "4px",
            }}>
              {value.name}
            </Typography>
            <img
              src={URL.createObjectURL(value)}
              alt={value.name}
              style={{ 
                maxWidth: "80px", 
                maxHeight: "60px",
                borderRadius: "6px",
                border: darkMode 
                  ? "1px solid rgba(75, 85, 99, 0.3)" 
                  : "1px solid rgba(229, 231, 235, 0.3)",
              }}
            />
          </div>
        );
      } else {
        return (
          <Typography variant="body2" sx={{
            color: darkMode ? "rgba(243, 244, 246, 1)" : "rgba(17, 24, 39, 1)",
          }}>
            {value.name}
          </Typography>
        );
      }
    }

    return (
      <Typography variant="body2" sx={{
        color: darkMode ? "rgba(243, 244, 246, 1)" : "rgba(17, 24, 39, 1)",
      }}>
        {value || "-"}
      </Typography>
    );
  };

  return (
    <TableContainer component={Paper} sx={tableContainerStyles}>
      <Table>
        <TableHead sx={tableHeaderStyles}>
          <TableRow>
            {columns.map((column, index) => (
              <TableCell
                key={index}
                sx={{
                  color: darkMode ? "rgba(243, 244, 246, 1) !important" : "rgba(17, 24, 39, 1) !important",
                  borderRight: index < columns.length - 1
                    ? darkMode 
                      ? "1px solid rgba(75, 85, 99, 0.3)"
                      : "1px solid rgba(229, 231, 235, 0.3)"
                    : "none",
                  fontWeight: "600 !important",
                }}
              >
                {column.title}
                {column?.required && (
                  <span style={{ 
                    color: darkMode ? "rgba(248, 113, 113, 1)" : "rgba(220, 38, 38, 1)", 
                    paddingLeft: "5px" 
                  }}>
                    *
                  </span>
                )}
              </TableCell>
            ))}
            <TableCell
              sx={{
                color: darkMode ? "rgba(243, 244, 246, 1) !important" : "rgba(17, 24, 39, 1) !important",
                borderLeft: darkMode 
                  ? "1px solid rgba(75, 85, 99, 0.3)" 
                  : "1px solid rgba(229, 231, 235, 0.3)",
                fontWeight: "600 !important",
                textAlign: "center",
              }}
            >
              Actions
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody sx={tableBodyStyles}>
          {data?.map((row, rowIndex) => (
            <TableRow key={rowIndex}>
              {columns?.map((column, colIndex) => (
                <TableCell
                  key={colIndex}
                  sx={{
                    color: darkMode ? "rgba(243, 244, 246, 1) !important" : "rgba(17, 24, 39, 1) !important",
                    borderRight: colIndex < columns?.length - 1
                      ? darkMode 
                        ? "1px solid rgba(75, 85, 99, 0.2)"
                        : "1px solid rgba(229, 231, 235, 0.2)"
                      : "none",
                    padding: "12px",
                  }}
                >
                  {renderCellContent(row, column)}
                </TableCell>
              ))}
              <TableCell
                sx={{
                  borderLeft: darkMode 
                    ? "1px solid rgba(75, 85, 99, 0.2)" 
                    : "1px solid rgba(229, 231, 235, 0.2)",
                  textAlign: "center",
                  padding: "8px",
                }}
              >
                <Box sx={{ display: "flex", justifyContent: "center", gap: "4px" }}>
                  <IconButton
                    onClick={() => onEdit(rowIndex, row)}
                    sx={iconButtonStyles}
                    size="small"
                  >
                    <Edit fontSize="small" />
                  </IconButton>
                  <IconButton
                    onClick={() => onDelete(rowIndex)}
                    sx={deleteButtonStyles}
                    size="small"
                  >
                    <Delete fontSize="small" />
                  </IconButton>
                </Box>
              </TableCell>
            </TableRow>
          ))}
          {data?.length === 0 && (
            <TableRow>
              <TableCell 
                colSpan={columns.length + 1} 
                sx={{ 
                  textAlign: "center", 
                  padding: "2rem",
                  color: darkMode ? "rgba(156, 163, 175, 1)" : "rgba(107, 114, 128, 1)",
                }}
              >
                <Typography variant="body1">
                  No data available
                </Typography>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
};


export default DynamicTable;
